import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, AlertCircle } from 'lucide-react';
import { getFileType, getFileIcon } from '../utils/fileHelpers';
import { useAuthStore } from '../store/authStore';

const SecureFileViewer = ({ fileName, onClose, isOpen }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuthStore();

  // Manejar tanto el formato antiguo (string) como el nuevo (objeto)
  const fileUrl = typeof fileName === 'object' ? fileName?.url : fileName;
  const displayName = typeof fileName === 'object' ? fileName?.name : fileName;

  const getSecureUrl = useCallback(async () => {
    if (!fileUrl) return;

    setIsLoading(true);
    setError(null);
    setSignedUrl(null);

    const type = getFileType(displayName || fileUrl);
    setFileType(type);

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
      const apiUrl = `${baseUrl}/api/evidences/secure-url?fileName=${encodeURIComponent(fileUrl)}`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        let errorDetails = 'No se pudo obtener la URL del archivo.';
        try {
          const errorData = await response.json();
          if (errorData.details) {
            errorDetails = errorData.details;
          } else if (errorData.error) {
            errorDetails = errorData.error;
          }
        } catch (e) {
          // The response was not JSON, just use the status text
          errorDetails = response.statusText;
        }
        throw new Error(`Error ${response.status}: ${errorDetails}`);
      }

      const data = await response.json();
      setSignedUrl(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [fileUrl, displayName, token]);

  useEffect(() => {
    if (isOpen && fileUrl) {
      getSecureUrl();
    } else if (!isOpen) {
      setSignedUrl(null);
      setError(null);
      setIsLoading(false);
      setFileType(null);
    }
  }, [isOpen, fileUrl, getSecureUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <span className="text-2xl mr-3">{getFileIcon(displayName || fileUrl)}</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{displayName || fileUrl}</h2>
              <p className="text-sm text-gray-500">
                {fileType && `Tipo: ${fileType.charAt(0).toUpperCase() + fileType.slice(1)}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {isLoading && (
            <div className="py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Preparando descarga...</p>
            </div>
          )}

          {error && (
            <div className="py-8">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">Error: {error}</p>
              <button
                onClick={getSecureUrl}
                className="btn-secondary"
              >
                Reintentar
              </button>
            </div>
          )}

          {signedUrl && !error && (
            <div className="py-8">
                <button 
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
                      const response = await fetch(`${baseUrl}${signedUrl}`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (!response.ok) {
                        throw new Error('Error al descargar el archivo');
                      }
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = displayName || fileUrl;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (err) {
                      setError('Error al descargar el archivo: ' + err.message);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className="btn-primary inline-flex items-center disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isLoading ? 'Descargando...' : `Descargar ${displayName || fileUrl}`}
                </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecureFileViewer;
